import type { BlockNoteEditor } from "@blocknote/core";
import { CommentsExtension } from "@blocknote/core/comments";
import { TextSelection } from "prosemirror-state";
import { useEffect, useRef } from "react";
import {
  removeCommentMarksForThread,
  syncPendingThreadAnchor,
} from "./editorCommentAnchorSync";
import {
  applyCommentMarkToQuote,
  applyCommentMarkToSelection,
  restoreAnchorsOnEditor,
} from "./editorCommentMarks";
import { bindEditorCommentPointers } from "./editorCommentPointer";
import { selectThreadInEditor } from "./editorCommentSelection";
import { getThreadQuote as getThreadQuoteFromEditor } from "./editorCommentThread";
import {
  canUseEditorView,
  captureEditorSelection,
  getCommentsExtension,
  type EditorCommentsConfig,
  type PendingCommentSelection,
} from "./editorComments";

export const createEditorCommentsExtension = (comments: EditorCommentsConfig) =>
  CommentsExtension({
    threadStore: comments.threadStore,
    resolveUsers: comments.resolveUsers,
    confirmBeforeDiscard: false,
  });

export function useEditorComments(
  editor: BlockNoteEditor,
  comments?: EditorCommentsConfig
) {
  const commentsRef = useRef(comments);
  commentsRef.current = comments;
  const pendingSelectionRef = useRef<PendingCommentSelection | null>(null);
  const lastSelectedThreadRef = useRef<string | undefined>(undefined);

  const startPendingCommentRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (!comments) return;
    const extension = getCommentsExtension(editor);
    if (!extension) return;

    return extension.store.subscribe(() => {
      const state = extension.store.state;
      if (state.pendingComment) {
        const selection =
          pendingSelectionRef.current || captureEditorSelection(editor);
        if (selection) {
          pendingSelectionRef.current = selection;
          commentsRef.current?.onPendingComment?.(
            selection.text,
            selection.threadId
          );
        }
      }
      if (state.selectedThreadId !== lastSelectedThreadRef.current) {
        lastSelectedThreadRef.current = state.selectedThreadId;
        commentsRef.current?.onSelectedThreadChange?.(state.selectedThreadId);
      }
    });
  }, [comments, editor]);

  useEffect(() => {
    if (!comments) return;
    return bindEditorCommentPointers(editor, () => {
      startPendingCommentRef.current();
    });
  }, [comments, editor]);

  const startPendingComment = (options?: { silent?: boolean }) => {
    if (!canUseEditorView(editor)) return;
    const selection = captureEditorSelection(editor);
    if (!selection) {
      // if (!options?.silent) {
      //   ToastService.info("Select text to add a comment");
      // }
      return;
    }
    pendingSelectionRef.current = selection;
    applyCommentMarkToSelection(editor, selection.threadId, selection, {
      stopPending: false,
    });
    try {
      editor.transact((tr) => {
        const pos = Math.min(selection.to, tr.doc.content.size);
        tr.setSelection(TextSelection.create(tr.doc, pos, pos));
      });
    } catch {
      // Keep the mark even if the caret cannot be moved.
    }
    try {
      getCommentsExtension(editor)?.selectThread(selection.threadId, false);
    } catch {
      // Highlight is optional; the thread mark is already on the text.
    }
    commentsRef.current?.onPendingComment?.(selection.text, selection.threadId);
    try {
      getCommentsExtension(editor)?.startPendingComment();
    } catch {
      return;
    }
  };

  const stopPendingComment = () => {
    const pending = pendingSelectionRef.current;
    pendingSelectionRef.current = null;
    if (pending) {
      removeCommentMarksForThread(editor, pending.threadId);
    }
    try {
      getCommentsExtension(editor)?.stopPendingComment();
      getCommentsExtension(editor)?.selectThread(undefined, false);
    } catch {
      return;
    }
  };

  const syncPendingAnchor = () => {
    const pending = pendingSelectionRef.current;
    if (!pending) return;
    const live = syncPendingThreadAnchor(editor, pending.threadId);
    if (!live) {
      pendingSelectionRef.current = null;
      commentsRef.current?.onPendingQuoteChange?.("", pending.threadId);
      return;
    }
    const quoteChanged = live.text !== pending.text;
    pendingSelectionRef.current = { ...pending, ...live };
    if (quoteChanged) {
      commentsRef.current?.onPendingQuoteChange?.(live.text, pending.threadId);
    }
  };

  const selectThread = (threadId: string | undefined, quote?: string) => {
    selectThreadInEditor(editor, threadId, quote);
  };

  const getThreadQuote = (threadId: string) =>
    getThreadQuoteFromEditor(editor, threadId);

  const applyCommentMark = (threadId: string, quote?: string) => {
    if (!canUseEditorView(editor)) return;
    const live = syncPendingThreadAnchor(editor, threadId);
    pendingSelectionRef.current = null;
    if (live) return;
    if (quote) {
      applyCommentMarkToQuote(editor, threadId, quote);
    }
  };

  const restoreCommentMarks = (
    anchors: Array<{ threadId: string; quote: string }>
  ) => {
    restoreAnchorsOnEditor(editor, anchors);
  };

  startPendingCommentRef.current = () => startPendingComment({ silent: true });

  return {
    startPendingComment,
    stopPendingComment,
    syncPendingAnchor,
    selectThread,
    applyCommentMark,
    restoreCommentMarks,
    getThreadQuote,
  };
}
